import React from "react";
import {
  ContentLayout,
  DebouncedSearch,
  HeaderBreadcrumbs,
  LoadingButton,
  ServerPaginatedTable,
  usePermission,
} from "../../../shared";
import { usePromosAndNotificationsList } from "../hooks/usePromosAndNotificationsList";
import { Button, Chip, Divider, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import { PromosAndNotificationFilterSection } from "../components";

const statusStyles = {
  published: { backgroundColor: "#E1FBF2", color: "#0F875B" },
  draft: { backgroundColor: "#1560D414", color: "#1560D4" },
  ended: { backgroundColor: "#FFE7E3", color: "#C42B1C" },
  unpublished: { backgroundColor: "#0000000F", color: "#A3A3A3" },
};

export const PromosAndNotificationsList = () => {
  const {
    breadcrumbLinks,
    state,
    statusOptions,
    storeOptions,
    typeOptions,
    gotoPage,
    setPageSize,
    onTableSearch,
    onSortChange,
    onFilterChange,
    onApplyFilter,
    onClearFilter,
    onClearAllFilter,
  } = usePromosAndNotificationsList();
  const navigate = useNavigate();
  const isCreatable = usePermission("promo-notification-create");

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Label",
        accessor: "label",
        Cell: ({row:{original}}) => original?.label || "-"
      },
      {
        Header: "Type",
        accessor: "type",
      },
      {
        Header: "Stores",
        accessor: "stores",
        disableSortBy: true,
        Cell: ({ value }) => {
          const stores = value.length > 3 ? value.slice(0, 3) : value;
          if (value.length > 3) {
            stores.push({ id: "more", name: `+${value.length - 3}` });
          }
          return (
            <Stack direction="row" spacing={1}>
              {stores?.length === 0
                ? "All Stores"
                : stores.map((store) => (
                    <Chip
                      key={store.id}
                      label={store.name}
                      size="small"
                      sx={{ borderRadius: "8px" }}
                    />
                  ))}
            </Stack>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          const chipStyle =
            statusStyles[value.toLowerCase()] || statusStyles["unpublished"];

          return (
            <Chip
              label={value}
              sx={{
                backgroundColor: chipStyle.backgroundColor,
                color: chipStyle.color,
              }}
            />
          );
        },
      },
      {
        Header: "Action",
        align: "right",
        disableSortBy: true,
        accessor: (row) => {
          return <img alt="details" src="/icons/arrow_forward.svg" />;
        },
      },
    ],
    [],
  );

  return (
    <ContentLayout
      title="Promos & Notifications"
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbLinks} />}
      rightContent={
        isCreatable && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => navigate("create")}
          >
            Create
          </Button>
        )
      }
    >
      <ServerPaginatedTable
        searchBar={true}
        columns={columns}
        defaultSearch={false}
        pagination={state.pagination}
        isLoading={state.tableLoading}
        data={state.promosAndNotifications}
        gotoPage={gotoPage}
        setPageSize={setPageSize}
        handleSort={onSortChange}
        onFilterClear={onClearFilter}
        onAllFilterClear={onClearAllFilter}
        sortByGlobal={state.sortBy}
        filterContent={<DebouncedSearch value={state.search} onChange={onTableSearch} />}
        filterApplied={state.appliedFilters}
        filterSection={
          <>
            <Stack display="flex" flexDirection="row" gap="12px">
              <PromosAndNotificationFilterSection
                storeOptions={storeOptions}
                statusOptions={statusOptions}
                typeOptions={typeOptions}
                onChange={onFilterChange}
                state={state.filter}
              />
              <LoadingButton
                variant="contained"
                size="medium"
                loading={false}
                onClick={onApplyFilter}
                disabled={!state.isFilterApplied}
              >
                Apply
              </LoadingButton>
            </Stack>
            <Stack direction="row" gap="12px">
              <Typography sx={{ color: "#0F875B", fontWeight: 600 }}>
                Published Promos: {state.totalCount.total_published_promos}
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography sx={{ color: "#0F875B", fontWeight: 600 }}>
                Published Notifications:{" "}
                {state.totalCount.total_published_notifications}
              </Typography>
            </Stack>
          </>
        }
        onRowLink={(row) => `${row.id}`}
      />
    </ContentLayout>
  );
};
